<template>
	<v-card>
		<v-card-title class="mt-4 mb-3">
			<h2>Parametry oceny</h2>
		</v-card-title>
		<v-btn class="mt-3 ml-4"
			primary @click="saveSettings()">Zapisz
		</v-btn>
		<v-card-text>
			<v-layout row>
				<v-flex xs12 v-if="contestData.evaluationOpen" class="ma-3">
					<v-alert :value="true" type="info" color="info">
						Rozpoczęto ocenę ofert w konkursie - możliwość edycji parametrów oceny została zablokowana.
					</v-alert>
				</v-flex>
			</v-layout>
			<v-layout row ml-3 mt-3>
				<v-flex xs5>
					<div style="font-weight: bold;">
						{{ ratingSettings.booleanEvaluation ===
							true ?
								"Ocena tak/nie" : "Ocena punktowa"}}
					</div>
				</v-flex>
				<v-flex xs7>
					<v-btn
						:disabled="contestData.evaluationOpen"
						flat icon
						color="green"
						@click="changeBooleanEvaluation()">
						<v-icon>cached</v-icon>
					</v-btn>
					<v-tooltip bottom max-width="480px">
						<template v-slot:activator="{ on }">
							<v-icon color="primary" dark v-on="on">info</v-icon>
						</template>
						<span>
							Zmiana typu oceny po rozpoczęciu opiniowania nie jest możliwa.
							Przed taką zmianą konieczne jest skasowanie wszystkich przypisanych wcześniej ocen merytorycznych i końcowych,
							a następnie ich ponowne przypisanie opiniującym. Przed skasowaniem oceny należy pamiętać o skopiowaniu jej treści.
						</span>
					</v-tooltip>
					<v-tooltip bottom max-width="480px" v-if="!ratingSettings.booleanEvaluation">
						<template v-slot:activator="{ on }">
							<v-icon class="ml-3" color="primary" dark v-on="on">info</v-icon>
						</template>
						<span>
							Na ocenę merytoryczną składa się indywidualna ocena członków komisji oraz ocena komisji.
							Na ocenę końcową składa się ocena merytoryczna oraz ocena współpracy.
							Ocena współpracy nie jest uwzględniana podczas kwalifikacji oferty na posiedzenie komisji -
							należy o tym pamiętać określając procentowy próg kwalifikacji.
						</span>
					</v-tooltip>
				</v-flex>
			</v-layout>

			<v-expand-transition>
				<div v-show="ratingSettings.booleanEvaluation !== true">
					<v-divider class="ma-4"/>
					<div class="ml-3 mb-5">
						<div style="font-weight: bold; font-size: 14px" class="mt-4 ml-1 mb-4">Dopuszczalna rozpiętość ocen indywidualnych</div>
						<v-layout row>
							<v-flex xs5>
								<v-text-field
									aria-label="max allowed points difference"
									browser-autocomplete="off"
									type="number"
									min="0"
									label="Maksymalna różnica punktowa"
									for="contest-rating-parameters-view-max-score-difference"
									id="contest-rating-parameters-view-max-score-difference"
									:disabled="contestData.evaluationOpen"
									v-model="ratingSettings.maxScoreDifference"
								></v-text-field>
							</v-flex>
							<v-flex xs7 style="margin-left: 90px">
								<v-tooltip bottom max-width="480px">
									<template v-slot:activator="{ on }">
										<v-icon color="primary" dark v-on="on">info</v-icon>
									</template>
									<span>
										Jeżeli różnica między indywidualnymi ocenami członków komisji będzie większa, to taka informacja
										zostanie wyświetlona w zakładce "Ocena ofert". Stanowi to sugestię wyznaczenia trzeciego
										opiniującego (nie zostanie on wybrany automatycznie).
									</span>
								</v-tooltip>
							</v-flex>
						</v-layout>

						<div style="font-weight: bold; font-size: 14px" class="mt-4 ml-1 mb-4">Kwalifikacja na posiedzenie komisji</div>
						<v-layout row>
							<v-flex xs5>
								<div>
									{{ ratingSettings.qualificationThresholdExpressedInPercentage ===
										true ?
											"Próg procentowy (z maksymalnej oceny merytorycznej)" : "Próg punktowy"}}
								</div>
							</v-flex>
							<v-flex xs7>
								<v-btn flat icon color="green"
									@click="changeQualificationExpression()"
									:disabled="contestData.evaluationOpen">
									<v-icon>cached</v-icon>
								</v-btn>
							</v-flex>
						</v-layout>

						<v-layout row>
							<v-flex xs5>
								<egranty-number-input-field
									v-model="ratingSettings.qualificationThreshold"
									label="Próg kwalifikacji oferty"
									for="contest-rating-parameters-view-qualification-threshold"
									id="contest-rating-parameters-view-qualification-threshold"
									:disabled="contestData.evaluationOpen"
								></egranty-number-input-field>
							</v-flex>
							<v-flex xs7 style="margin-left: 90px">
								<v-tooltip bottom max-width="480px">
									<template v-slot:activator="{ on }">
										<v-icon color="primary" dark v-on="on">info</v-icon>
									</template>
									<span>
										Oferty, których średnia indywidualnych ocen członków komisji nie osiągnęła progu kwalifikacji,
										nie są wyświetlane w zbiorczej tabeli ocen.
									</span>
								</v-tooltip>
							</v-flex>
						</v-layout>

						<div style="font-weight: bold; font-size: 14px" class="mt-4 ml-1 mb-4">Rekomendacja do dofinansowania</div>
						<v-layout row>
							<v-flex xs5>
								<div>
									{{ ratingSettings.recommendationThresholdExpressedInPercentage ===
										true ?
											"Próg procentowy (z maksymalnej oceny końcowej)" : "Próg punktowy"}}
								</div>
							</v-flex>
							<v-flex xs7>
								<v-btn flat icon color="green"
									@click="changeRecommendationExpression()"
									:disabled="contestData.evaluationOpen">
									<v-icon>cached</v-icon>
								</v-btn>
							</v-flex>
						</v-layout>

						<v-layout row>
							<v-flex xs5>
								<egranty-number-input-field
									v-model="ratingSettings.recommendationThreshold"
									label="Próg rekomendacji do dofinansowania"
									for="contest-rating-parameters-view-recommendation-threshold"
									id="contest-rating-parameters-view-recommendation-threshold"
									:disabled="contestData.evaluationOpen"
								></egranty-number-input-field>
							</v-flex>
							<v-flex xs7 style="margin-left: 90px">
								<v-tooltip bottom max-width="480px">
									<template v-slot:activator="{ on }">
										<v-icon color="primary" dark v-on="on">info</v-icon>
									</template>
									<span>
										Oferty, których łączna ocena końcowa nie osiągnęła progu rekomendacji, nie mogą być rekomendowane
										przez komisję do dofinansowania.
									</span>
								</v-tooltip>
							</v-flex>
						</v-layout>
					</div>

					<v-divider class="ma-4"/>

					<h4 class="ma-4">Parametry oceny komisji</h4>

					<div>
						<v-layout ml-4 mt-5>
							<h4>Pierwsze kryterium oceny komisji</h4>
						</v-layout>
						<v-layout ml-4 row wrap>
							<v-flex xs3>
								<v-text-field
									browser-autocomplete="off"
									label="Nazwa"
									aria-label="first committee criteria name"
									for="contest-rating-parameters-view-first-name-criteria"
									id="contest-rating-parameters-view-first-name-criteria"
									v-model="ratingSettings.firstCommitteeCriteriaName"
									:disabled="contestData.evaluationOpen"
								></v-text-field>
							</v-flex>
							<v-flex xs2/>
							<v-flex xs3>
								<egranty-number-input-field
									v-model="ratingSettings.firstCommitteeCriteriaMultiplier"
									label="Mnożnik"
									for="contest-rating-parameters-view-first-name-multiplier"
									id="contest-rating-parameters-view-first-name-multiplier"
									:disabled="contestData.evaluationOpen"
								></egranty-number-input-field>
							</v-flex>
						</v-layout>
						<v-layout ml-4 row wrap>
							<v-flex xs3>
								<v-text-field
									browser-autocomplete="off"
									label="Minimalna wartość"
									aria-label="first committee criteria minimum points"
									for="contest-rating-parameters-view-first-name-min"
									id="contest-rating-parameters-view-first-name-min"
									v-model="ratingSettings.firstCommitteeCriteriaMinPoints"
									:disabled="contestData.evaluationOpen"
								></v-text-field>
							</v-flex>
							<v-flex xs2/>
							<v-flex xs3>
								<v-text-field
									browser-autocomplete="off"
									label="Maksymalna wartość"
									for="contest-rating-parameters-view-first-name-max"
									id="contest-rating-parameters-view-first-name-max"
									aria-label="first committee criteria maximum points"
									v-model="ratingSettings.firstCommitteeCriteriaMaxPoints"
									:disabled="contestData.evaluationOpen"
								></v-text-field>
							</v-flex>
						</v-layout>
						<v-layout ml-4 mt-4>
							<h4>Drugie kryterium oceny komisji</h4>
						</v-layout>
						<v-layout ml-4 row wrap>
							<v-flex xs3>
								<v-text-field
									browser-autocomplete="off"
									label="Nazwa"
									for="contest-rating-parameters-view-second-name-criteria"
									id="contest-rating-parameters-view-second-name-criteria"
									aria-label="second committee criteria name"
									v-model="ratingSettings.secondCommitteeCriteriaName"
									:disabled="contestData.evaluationOpen"
								></v-text-field>
							</v-flex>
							<v-flex xs2/>
							<v-flex xs3>
								<egranty-number-input-field
									v-model="ratingSettings.secondCommitteeCriteriaMultiplier"
									label="Mnożnik"
									for="contest-rating-parameters-view-second-name-multiplier"
									id="contest-rating-parameters-view-second-name-multiplier"
									:disabled="contestData.evaluationOpen"
								></egranty-number-input-field>
							</v-flex>
							<v-flex xs1/>

						</v-layout>
						<v-layout ml-4 row wrap>
							<v-flex xs3>
								<v-text-field
									browser-autocomplete="off"
									label="Minimalna wartość"
									for="contest-rating-parameters-view-second-name-min"
									id="contest-rating-parameters-view-second-name-min"
									aria-label="second committee criteria minimum points"
									v-model="ratingSettings.secondCommitteeCriteriaMinPoints"
									:disabled="contestData.evaluationOpen"
								></v-text-field>
							</v-flex>
							<v-flex xs2/>
							<v-flex xs3>
								<v-text-field
									browser-autocomplete="off"
									label="Maksymalna wartość"
									for="contest-rating-parameters-view-second-name-max"
									id="contest-rating-parameters-view-second-name-max"
									aria-label="second committee criteria maximum points"
									v-model="ratingSettings.secondCommitteeCriteriaMaxPoints"
									:disabled="contestData.evaluationOpen"
								></v-text-field>
							</v-flex>
						</v-layout>
					</div>

					<egranty-table
						:table-name="'Parametry indywidualnej opinii członka komisji'"
						v-show="ratingParametersAppliable === true"
						:items="ratingParameters"
						:headers="headers"
						:toolbar-action-name="'Dodaj parametr oceny'"
						:toolbar-action-visible="!contestData.evaluationOpen"
						:row-actions="rowActions"
						@toolbarAction="addContestRatingParametersVisible = true"
						@edit="editRatingParameter"
						@delete="deleteRatingParameter"
					></egranty-table>
					<contest-rating-parameters-dialog
						:show-dialog.sync="addContestRatingParametersVisible"
						:edited-item="editedItem"
						@save="addRatingParameter"
					/>
				</div>
			</v-expand-transition>

			<div v-if="!ratingParametersAppliable && ratingSettings.booleanEvaluation !== true">
				Formularz indywidualnej opinii członka komisji nie został wybrany dla tego konkursu, więc parametry
				indywidualnej opinii zostały ukryte.
			</div>
		</v-card-text>
	</v-card>
</template>

<script>
	import EgrantyTable from "../../../genericComponents/EgrantyTable";
	import ContestRatingParametersDialog from "./components/ContestRatingParametersDialog";
	import {RatingParameter} from "@/api/generated/RatingParameter";
	import egrantyNumberInputField from "../../../genericComponents/EgrantyNumberInputField";
	import {PublicContest} from "@/api/generated/PublicContest";

	export default {
		name: "ContestRatingParametersView",

		components: {
			EgrantyTable, ContestRatingParametersDialog, egrantyNumberInputField,
		},

		data: function () {
			return {
				editedItem: undefined,
				ratingParametersAppliable: false,
				ratingParameters: [],
				contestData: {
					id: this.$route.params.id,
					contestStatus: null,
				},
				contestId: this.$route.params.id,
				headers: [
					{text: "Nazwa kryterium", value: "criteriaName"},
					{text: "Minimalna wartość", value: "minRating", type: "NUMBER", align: "left"},
					{text: "Maksymalna wartość", value: "maxRating", type: "NUMBER", align: "left"},
					{text: "Mnożnik", value: "multiplier", type: "NUMBER", align: "left"},
				],
				actions: [
					{name: "edit", icon: "edit"},
					{name: "delete", icon: "delete"},
				],
				addContestRatingParametersVisible: false,
				ratingSettings: {
					booleanEvaluation: false,
					qualificationThreshold: null,
					qualificationThresholdExpressedInPercentage: false,
					recommendationThreshold: null,
					recommendationThresholdExpressedInPercentage: false,
					maxScoreDifference: null,
					firstCommitteeCriteriaName: null,
					secondCommitteeCriteriaName: null,
				},
        initialEvaluationIsBoolean: false,
			};
		},

		computed: {
			rowActions() {
				if (this.contestData.evaluationOpen) {
					return [];
				}
				return this.actions;
			},
		},

		beforeMount() {
			this.update();
			PublicContest.getStatus(this, this.contestId, response => {
				this.contestData = response;
			});
		},

		methods: {
			deleteRatingParameter(ratingParameters) {
				RatingParameter.delete(this, ratingParameters.id, successResponse => {
						this.$messagesBus.$emit("success", "Parametr oceny został pomyślnie usunięty", successResponse);
						this.loadRatingParameters();
					},
					errorResponse => {
						this.$messagesBus.$emit("error", errorResponse.message, errorResponse);
					});
			},

			addRatingParameter(newRatingParametersForm) {
				if (!this.editedItem) {
					this.createRatingParameter(newRatingParametersForm);
				} else {
					this.updateRatingParameter(newRatingParametersForm);
				}
			},

			createRatingParameter(newRatingParametersForm) {
				newRatingParametersForm.publicContestId = this.contestId;
				RatingParameter.post(this, newRatingParametersForm, successResponse => {
						this.addContestRatingParametersVisible = false;
						this.$messagesBus.$emit("success", "Parametr oceny został pomyślnie dodany", successResponse);
						this.loadRatingParameters();
					},
					errorResponse => {
						this.$messagesBus.$emit("error", "Nie udało się dodać parametru oceny", errorResponse);
					});
			},

			updateRatingParameter(newRatingParametersForm) {
				newRatingParametersForm.publicContestId = this.contestId;
				RatingParameter.put(this, newRatingParametersForm, successResponse => {
						this.addContestRatingParametersVisible = false;
						this.$messagesBus.$emit("success", "Parametr oceny został pomyślnie zmieniony", successResponse);
						this.loadRatingParameters();
					},
					errorResponse => {
						this.$messagesBus.$emit("error", errorResponse.message, errorResponse);
					});
				this.editedItem = undefined;
			},

			editRatingParameter(item) {
				this.editedItem = Object.assign({}, item);
				this.addContestRatingParametersVisible = true;
			},

			changeBooleanEvaluation() {
				this.ratingSettings.booleanEvaluation =
					!(this.ratingSettings.booleanEvaluation);
			},

			changeQualificationExpression() {
				this.ratingSettings.qualificationThresholdExpressedInPercentage =
					!(this.ratingSettings.qualificationThresholdExpressedInPercentage);
			},

			changeRecommendationExpression() {
				this.ratingSettings.recommendationThresholdExpressedInPercentage =
					!(this.ratingSettings.recommendationThresholdExpressedInPercentage);
			},

			saveSettings() {
        if (this.initialEvaluationIsBoolean !== this.ratingSettings.booleanEvaluation) {
          PublicContest.getIsSubstantiveOrFinalEvaluationPresent(this, this.contestId, successResponse => {
            if (successResponse) {
              this.$messagesBus.$emit("error", "Aby zmienić typ oceny, należy usunąć przypisane oceny merytoryczne i końcowe");
            } else {
              this.putRatingSettings();
            }
          }, () => {
            this.$messagesBus.$emit("error", "Nie udało się zwalidować ocen");
          });
        }
        else {
          this.putRatingSettings();
        }
			},

      putRatingSettings() {
        PublicContest.putRatingSettings(this, this.contestId, this.ratingSettings, () => {
          this.$messagesBus.$emit("success", "Parametry oceny zostały pomyślnie zapisane");
          this.loadRatingSettings();
        }, () => {
          this.$messagesBus.$emit("error", "Nie udało się zapisać parametrów oceny");
        });
      },

			loadRatingParameters() {
				RatingParameter.getAllRatingParameters(this, this.contestId, response => {
					this.ratingParameters = response;
				});
			},

			loadRatingSettings() {
				PublicContest.getRatingSettings(this, this.contestId, response => {
					this.ratingSettings = response.ratingSettings;
          this.initialEvaluationIsBoolean = response.ratingSettings.booleanEvaluation;
				});
			},

			checkIfRatingSettingsAppliable() {
				PublicContest.getFormStatus(this, this.$route.params.id, response => {
					this.ratingParametersAppliable = response.contestSchemaSet.individualEvaluationSchemaIncluded;
				});
			},

			update() {
				this.checkIfRatingSettingsAppliable();
				this.loadRatingParameters();
				this.loadRatingSettings();
			},
		},
	};
</script>

<style scoped>
	.v-toolbar {
		margin-bottom: 20px;
	}
</style>
